import { useState, type FormEvent, type ReactNode } from 'react';
import type { LoanType, PayeeDraft, PayeeKind, Repeat } from '@tahi/core';
import { Button } from '../../../ui';
import { Segmented } from '../../../ui/Segmented';
import { Switch } from '../../../ui/Switch';
import { TextField } from '../../../ui/TextField';
import { FIELD } from '../../../ui/TextField';

/** Same size as a TextField; keeps the native arrow. */
const SELECT = FIELD.replace('appearance-none ', '');
import { copy, errorText } from '../copy';
import { minorToInput, parsePesoInput } from '../format';

/** What the form edits, as typed. */
interface Values {
  name: string;
  amount: string;
  repeat: Repeat;
  everyN: string;
  day1: string;
  day2: string;
  startDate: string;
  count: string;
  autoPay: boolean;
  categoryId: string;
  fundId: string;
  accountId: string;
  issuer: string;
  last4: string;
  statementDay: string;
  /** Card: due on a fixed day of the month, or N days after the statement. */
  dueMode: 'day' | 'after';
  daysAfter: string;
  limit: string;
  opening: string;
  cardholder: string;
  lender: string;
  loanType: LoanType;
  principal: string;
  rate: string;
  note: string;
}

const money = (m: number | null) => (m == null ? '' : minorToInput(m));

function toValues(d: PayeeDraft): Values {
  return {
    name: d.name,
    amount: money(d.amountMinor),
    repeat: d.repeat,
    everyN: String(d.everyN),
    day1: d.dueDays[0] ? String(d.dueDays[0]) : '',
    day2: d.dueDays[1] ? String(d.dueDays[1]) : '',
    startDate: d.startDate,
    count: d.paymentCount ? String(d.paymentCount) : '',
    autoPay: d.autoPay,
    categoryId: d.categoryId ?? '',
    fundId: d.fundId ?? '',
    accountId: d.accountId ?? '',
    issuer: d.issuer ?? '',
    last4: d.last4 ?? '',
    statementDay: d.statementDay ? String(d.statementDay) : '',
    dueMode: d.dueDaysAfterStatement != null ? 'after' : 'day',
    daysAfter: d.dueDaysAfterStatement != null ? String(d.dueDaysAfterStatement) : '',
    limit: money(d.creditLimitMinor),
    opening: money(d.openingBalanceMinor),
    cardholder: d.cardholder ?? '',
    lender: d.lender ?? '',
    loanType: d.loanType ?? 'fixed',
    principal: money(d.principalMinor),
    rate: d.interestBp ? String(d.interestBp / 100) : '',
    note: d.note ?? '',
  };
}

const int = (s: string): number | null => (/^\d+$/.test(s.trim()) ? Number(s.trim()) : null);
const optMoney = (s: string): number | null => (s.trim() === '' ? null : (parsePesoInput(s) ?? -1));

function toDraft(base: PayeeDraft, v: Values): PayeeDraft {
  const flexible = base.kind === 'loan' && v.loanType === 'flexible';
  const days = [int(v.day1), int(v.day2)].filter((d): d is number => d !== null);
  const rate = v.rate.trim() === '' ? null : Number(v.rate.replace(',', '.'));
  return {
    ...base,
    name: base.kind === 'loan' && v.name.trim() === '' ? v.lender : v.name,
    amountMinor: optMoney(v.amount),
    repeat: flexible && v.amount.trim() === '' ? 'none' : v.repeat,
    everyN: int(v.everyN) ?? 1,
    dueDays: days,
    startDate: v.startDate,
    paymentCount: v.count.trim() === '' ? null : (int(v.count) ?? -1),
    autoPay: v.autoPay,
    categoryId: v.categoryId || null,
    fundId: v.fundId || null,
    accountId: v.accountId || null,
    issuer: v.issuer,
    last4: v.last4,
    statementDay: int(v.statementDay),
    dueDaysAfterStatement:
      base.kind === 'card' && v.dueMode === 'after' ? (int(v.daysAfter) ?? -1) : null,
    creditLimitMinor: optMoney(v.limit),
    openingBalanceMinor: optMoney(v.opening),
    cardholder: v.cardholder,
    lender: v.lender,
    loanType: base.kind === 'loan' ? v.loanType : null,
    principalMinor: optMoney(v.principal),
    interestBp: rate === null || Number.isNaN(rate) ? null : Math.round(rate * 100),
    note: v.note,
  };
}

export interface PayeeFormProps {
  initial: PayeeDraft;
  mode: 'add' | 'edit';
  categories: { id: string; name: string }[];
  funds: { id: string; name: string }[] | null;
  /** v0.5: money accounts for "Pays from" (null: Budget isn't on). */
  accounts?: { id: string; name: string }[] | null;
  /** The name of Budget's "Pay from" account, for the default option. */
  payFromName?: string | null;
  error?: string | null;
  validate(d: PayeeDraft): string | null;
  onSubmit(d: PayeeDraft): void | Promise<void>;
  onCancel(): void;
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange(v: string): void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="flex flex-col gap-1 text-sm font-medium text-muted">
      {label}
      <select className={SELECT} value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

const LOAN_TYPES: { value: LoanType; label: string; example: string }[] = [
  {
    value: 'fixed',
    label: 'Fixed payments',
    example: 'The same amount on set dates, e.g. a lending app or installments.',
  },
  {
    value: 'flexible',
    label: 'Pay any time',
    example: 'A balance you pay down when you can, e.g. a family loan with monthly interest.',
  },
];

const Row = ({ children }: { children: ReactNode }) => (
  <div className="grid grid-cols-2 items-start gap-3">{children}</div>
);

/** One form for every kind (spec §5 fields); only the kind's own fields show. */
export function PayeeForm({
  initial,
  mode,
  categories,
  funds,
  accounts = null,
  payFromName = null,
  error = null,
  validate,
  onSubmit,
  onCancel,
}: PayeeFormProps) {
  const [v, setV] = useState<Values>(() => toValues(initial));
  const [shownError, setShownError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof Values>(k: K, value: Values[K]) =>
    setV((s) => ({ ...s, [k]: value }));
  const kind: PayeeKind = initial.kind;
  const flexible = kind === 'loan' && v.loanType === 'flexible';
  const dec = { inputMode: 'decimal' as const, autoComplete: 'off' };
  const num = { inputMode: 'numeric' as const, autoComplete: 'off' };

  async function submit(e: FormEvent) {
    e.preventDefault();
    const draft = toDraft(initial, v);
    const err = validate(draft);
    if (err) {
      setShownError(errorText(err));
      return;
    }
    setShownError(null);
    setBusy(true);
    try {
      await onSubmit(draft);
    } finally {
      setBusy(false);
    }
  }

  const schedule = (
    <>
      <Segmented
        label="Repeats"
        value={v.repeat === 'none' ? 'monthly' : v.repeat}
        options={[
          { value: 'monthly', label: 'Monthly' },
          { value: 'weekly', label: 'Weekly' },
          { value: 'yearly', label: 'Yearly' },
        ]}
        onChange={(r) => set('repeat', r)}
      />
      {v.repeat === 'monthly' ? (
        <Row>
          <TextField
            label="Due day"
            {...num}
            value={v.day1}
            onChange={(e) => set('day1', e.target.value)}
          />
          <TextField
            label="Second day (optional)"
            hint="Twice a month, e.g. 3 and 18"
            {...num}
            value={v.day2}
            onChange={(e) => set('day2', e.target.value)}
          />
        </Row>
      ) : null}
      {v.repeat === 'yearly' ? (
        <TextField
          label="Due day"
          {...num}
          value={v.day1}
          onChange={(e) => set('day1', e.target.value)}
        />
      ) : null}
      <Row>
        <TextField
          label={
            kind === 'loan' && !flexible
              ? 'First payment'
              : flexible
                ? 'Balance as of'
                : 'First due date'
          }
          type="date"
          value={v.startDate}
          onChange={(e) => e.target.value && set('startDate', e.target.value)}
        />
        <TextField
          label={
            v.repeat === 'weekly'
              ? 'Every N weeks'
              : v.repeat === 'yearly'
                ? 'Every N years'
                : 'Every N months'
          }
          {...num}
          value={v.everyN}
          onChange={(e) => set('everyN', e.target.value)}
        />
      </Row>
    </>
  );

  const categoryField = (
    <Select
      label="Category"
      value={v.categoryId}
      onChange={(x) => set('categoryId', x)}
      options={[
        { value: '', label: kind === 'loan' ? 'Loan payment' : 'Bills & Utilities' },
        ...categories.map((c) => ({ value: c.id, label: c.name })),
      ]}
    />
  );
  // v0.5: a payment counts under a limit only when one is picked (never the default limit), so the
  // empty option says "No limit" instead of the old, wrong "Default fund".
  const limitField =
    funds && funds.length > 0 && kind !== 'card' ? (
      <Select
        label="Limit"
        value={v.fundId}
        onChange={(x) => set('fundId', x)}
        options={[
          { value: '', label: 'No limit' },
          ...funds.map((f) => ({ value: f.id, label: f.name })),
        ]}
      />
    ) : null;
  const accountField =
    accounts && accounts.length > 0 ? (
      <Select
        label="Pays from"
        value={v.accountId}
        onChange={(x) => set('accountId', x)}
        options={[
          {
            value: '',
            label: payFromName ? `Pay from account (${payFromName})` : 'Pay from account',
          },
          ...accounts.map((a) => ({ value: a.id, label: a.name })),
        ]}
      />
    ) : null;
  const fundField = (
    <>
      {accountField}
      {limitField}
    </>
  );

  return (
    <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4" noValidate>
      {kind === 'loan' ? (
        <>
          <div role="radiogroup" aria-label="What kind of loan?" className="flex flex-col gap-2">
            <span aria-hidden="true" className="text-sm font-medium text-muted">
              What kind of loan?
            </span>
            {LOAN_TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                role="radio"
                aria-checked={v.loanType === t.value}
                onClick={() => set('loanType', t.value)}
                className={`flex min-h-11 flex-col items-start rounded-2xl border px-4 py-3 text-left ${
                  v.loanType === t.value
                    ? 'border-accent bg-secondary'
                    : 'border-divider bg-surface'
                } focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[var(--ring)]`}
              >
                <span className="font-medium text-fg">{t.label}</span>
                <span className="text-sm text-muted">{t.example}</span>
              </button>
            ))}
          </div>
          <TextField
            label="Lender (who is it from?)"
            value={v.lender}
            onChange={(e) => set('lender', e.target.value)}
          />
          <TextField
            label="Name (optional)"
            hint="Tells two loans from the same lender apart"
            value={v.name}
            onChange={(e) => set('name', e.target.value)}
          />
        </>
      ) : (
        <TextField
          label="Name"
          placeholder={kind === 'card' ? 'BDO' : kind === 'utility' ? 'Electricity' : 'Rent'}
          value={v.name}
          onChange={(e) => set('name', e.target.value)}
        />
      )}

      {kind === 'bill' || kind === 'utility' ? (
        <>
          <TextField
            label={kind === 'utility' ? 'Starting estimate' : 'Amount'}
            hint={kind === 'utility' ? 'Replaced by the average of your last 3 bills' : undefined}
            {...dec}
            value={v.amount}
            onChange={(e) => set('amount', e.target.value)}
          />
          {schedule}
          <TextField
            label="Number of payments (optional)"
            hint="Leave blank if it doesn't end"
            {...num}
            value={v.count}
            onChange={(e) => set('count', e.target.value)}
          />
          <div className="flex items-center justify-between gap-3">
            <span aria-hidden="true" className="text-fg">
              Paid automatically (auto-debit)
            </span>
            <Switch
              label="Paid automatically (auto-debit)"
              checked={v.autoPay}
              onCheckedChange={(c) => set('autoPay', c)}
            />
          </div>
          {categoryField}
          {fundField}
        </>
      ) : null}

      {kind === 'card' ? (
        <>
          <TextField
            label="Statement day"
            {...num}
            value={v.statementDay}
            onChange={(e) => set('statementDay', e.target.value)}
          />
          <div className="flex flex-col gap-2">
            <span aria-hidden="true" className="text-sm font-medium text-muted">
              Due date
            </span>
            <Segmented
              label="Due date"
              value={v.dueMode}
              options={[
                { value: 'day', label: 'Day of the month' },
                { value: 'after', label: 'After the statement' },
              ]}
              onChange={(m) => set('dueMode', m)}
            />
          </div>
          {v.dueMode === 'day' ? (
            <TextField
              label="Payment due day"
              hint="Day of the month, e.g. 5"
              {...num}
              value={v.day1}
              onChange={(e) => set('day1', e.target.value)}
            />
          ) : (
            <TextField
              label="Days after the statement"
              hint="e.g. 25: a statement on Oct 13 is due Nov 7"
              {...num}
              value={v.daysAfter}
              onChange={(e) => set('daysAfter', e.target.value)}
            />
          )}
          <TextField
            label="Current balance (optional)"
            hint="What you owe now. Not counted as spending (it was spent before Tahi), but Budget holds it back until it's paid."
            {...dec}
            value={v.opening}
            onChange={(e) => set('opening', e.target.value)}
          />
          <Row>
            <TextField
              label="Credit limit (optional)"
              {...dec}
              value={v.limit}
              onChange={(e) => set('limit', e.target.value)}
            />
            <TextField
              label="Last 4 digits (optional)"
              {...num}
              maxLength={4}
              value={v.last4}
              onChange={(e) => set('last4', e.target.value)}
            />
          </Row>
          <TextField
            label="Issuer (optional)"
            value={v.issuer}
            onChange={(e) => set('issuer', e.target.value)}
          />
          <TextField
            label="Main cardholder (optional)"
            hint="For a supplementary card: who you pay your share to, e.g. Papa"
            value={v.cardholder}
            onChange={(e) => set('cardholder', e.target.value)}
          />
          <div className="flex items-center justify-between gap-3">
            <span aria-hidden="true" className="text-fg">
              Paid automatically (auto-debit)
            </span>
            <Switch
              label="Paid automatically (auto-debit)"
              checked={v.autoPay}
              onCheckedChange={(c) => set('autoPay', c)}
            />
          </div>
          {fundField}
        </>
      ) : null}

      {kind === 'loan' && !flexible ? (
        <>
          <TextField
            label="Payment amount"
            {...dec}
            value={v.amount}
            onChange={(e) => set('amount', e.target.value)}
          />
          {schedule}
          <TextField
            label="Number of payments"
            hint="All of them from the first payment. Ones before today count as paid."
            {...num}
            value={v.count}
            onChange={(e) => set('count', e.target.value)}
          />
          <TextField
            label="Amount owed (optional)"
            {...dec}
            value={v.principal}
            onChange={(e) => set('principal', e.target.value)}
          />
          <div className="flex items-center justify-between gap-3">
            <span aria-hidden="true" className="text-fg">
              Paid automatically (auto-debit)
            </span>
            <Switch
              label="Paid automatically (auto-debit)"
              checked={v.autoPay}
              onCheckedChange={(c) => set('autoPay', c)}
            />
          </div>
          {categoryField}
          {fundField}
        </>
      ) : null}

      {flexible ? (
        <>
          <Row>
            <TextField
              label="Amount owed"
              {...dec}
              value={v.principal}
              onChange={(e) => set('principal', e.target.value)}
            />
            <TextField
              label="Interest per month % (optional)"
              hint="Blank = none"
              {...dec}
              value={v.rate}
              onChange={(e) => set('rate', e.target.value)}
            />
          </Row>
          <TextField
            label="Planned payment (optional)"
            hint="Shows as due on the days below, and projects the payoff"
            {...dec}
            value={v.amount}
            onChange={(e) => set('amount', e.target.value)}
          />
          {schedule}
          {categoryField}
          {fundField}
        </>
      ) : null}

      <TextField
        label="Note (optional)"
        value={v.note}
        onChange={(e) => set('note', e.target.value)}
      />

      {shownError || error ? (
        <p role="alert" className="text-sm text-destructive">
          {shownError ?? error}
        </p>
      ) : null}
      <div className="flex flex-col gap-2">
        <Button type="submit" variant="primary" block disabled={busy}>
          {mode === 'add' ? copy.add : copy.save}
        </Button>
        <Button variant="ghost" block onClick={onCancel}>
          {copy.cancel}
        </Button>
      </div>
    </form>
  );
}
